import { Activity, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

// =============================================================================
// What a screen reported about itself (migration 249). Rendered on the device
// card so a wall-mounted TV can be diagnosed from a desk: is it alive, is it on
// the live channel, what geometry is it actually painting, and which build of
// the web runtime and of the native shell is running there.
// =============================================================================

export interface DisplayRuntimeFacts {
  status?: string;
  observed_at?: string;
  board?: string;
  gateway?: string;
  overscan?: number;
  app?: string;
  screen?: { w?: number; h?: number; dpr?: number };
  viewport?: { w?: number; h?: number };
  native?: { app?: string; webview?: string; model?: string; android?: number } | null;
}

const STATUS_STYLE: Record<string, { label: string; className: string; Icon: typeof Activity }> = {
  ok: { label: 'Sprawny', className: 'text-emerald-400', Icon: CheckCircle2 },
  degraded: { label: 'Ograniczony', className: 'text-amber-400', Icon: Activity },
  down: { label: 'Awaria', className: 'text-red-400', Icon: AlertTriangle },
  unknown: { label: 'Nieznany', className: 'text-gray-400', Icon: Activity },
};

/** "przed chwilą" beats a timestamp when the question is "is it alive now?". */
function sinceLabel(iso: string | null | undefined): string {
  if (!iso) return 'brak sygnału';
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(seconds)) return 'brak sygnału';
  if (seconds < 90) return 'przed chwilą';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min temu`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h temu`;
  return `${Math.round(seconds / 86_400)} dni temu`;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-gray-500">{label}</p>
      <p className="text-gray-300">{value}</p>
    </div>
  );
}

export function DisplayDiagnostics({
  runtime,
  heartbeatAt,
}: {
  runtime: DisplayRuntimeFacts;
  heartbeatAt: string | null;
}) {
  const status = STATUS_STYLE[runtime.status ?? 'unknown'] ?? STATUS_STYLE.unknown;
  const { Icon } = status;

  const resolution =
    runtime.screen?.w && runtime.screen?.h
      ? `${runtime.screen.w}×${runtime.screen.h}${
          runtime.screen.dpr && runtime.screen.dpr !== 1 ? ` @${runtime.screen.dpr}x` : ''
        }`
      : '—';

  return (
    <div className="mt-4 pt-4 border-t border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${status.className}`} />
        <span className={`text-sm font-medium ${status.className}`}>{status.label}</span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {sinceLabel(heartbeatAt ?? runtime.observed_at)}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Fact label="Rozdzielczość" value={resolution} />
        <Fact
          label="Kanał na żywo"
          value={runtime.gateway === 'online' ? 'połączony' : (runtime.gateway ?? '—')}
        />
        <Fact label="Plansza" value={runtime.board ?? '—'} />
        <Fact
          label="Margines TV"
          value={typeof runtime.overscan === 'number' ? `${runtime.overscan}%` : '—'}
        />
        <Fact label="Wersja tablicy" value={runtime.app ?? '—'} />
        <Fact label="Aplikacja TV" value={runtime.native?.app ?? 'przeglądarka'} />
        <Fact label="WebView" value={runtime.native?.webview ?? '—'} />
        <Fact label="Urządzenie" value={runtime.native?.model ?? '—'} />
      </div>
    </div>
  );
}
